import React, { useState, useEffect } from 'react';
import { Cloud, CloudOff, Wifi, WifiOff, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { getSyncStatus, forceSyncNow } from '../hybridStorage';
import { isSignedIn as isDriveSignedIn, signInWithGoogle, ensureDriveSession } from '../googleDriveService';

const SyncStatus = ({ className = '' }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Load sync status
    loadSyncStatus();
    
    // Load last sync time
    const savedLastSync = localStorage.getItem('lastSyncTime');
    if (savedLastSync) {
      setLastSync(new Date(savedLastSync));
    }

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      loadSyncStatus();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      loadSyncStatus();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Refresh status every 30 seconds
    const interval = setInterval(loadSyncStatus, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      clearInterval(interval);
    };
  }, []);

  const handleServiceWorkerMessage = (event) => {
    if (event.data?.type === 'BACKGROUND_SYNC') {
      setLastSync(new Date());
      localStorage.setItem('lastSyncTime', new Date().toISOString());
      loadSyncStatus();
    }
  };

  const loadSyncStatus = async () => {
    try {
      const status = await getSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const handleManualSync = async () => {
    if (!isOnline) {
      alert('Cannot sync while offline. Please check your internet connection.');
      return;
    }

    setIsSyncing(true);
    try {
      const syncResult = await forceSyncNow();
      setLastSync(new Date());
      localStorage.setItem('lastSyncTime', new Date().toISOString());
      if (Array.isArray(syncResult?.transactions) && syncResult.transactions.length > 0) {
        window.dispatchEvent(new CustomEvent('ledger-transactions-synced', {
          detail: {
            transactions: syncResult.transactions,
            source: 'manual-sync'
          }
        }));
      }
      await loadSyncStatus();
    } catch (error) {
      console.error('Manual sync failed:', error);
      alert('Sync failed. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleReconnectDrive = async () => {
    try {
      await signInWithGoogle();
      const driveReady = await ensureDriveSession();
      await loadSyncStatus();
      if (driveReady) {
        window.dispatchEvent(new CustomEvent('drive-reconnected'));
      } else {
        alert('Google Drive permission was not granted. Please reconnect and allow Drive access before adding transactions.');
      }
    } catch (error) {
      console.error('Drive reconnect failed:', error);
      alert('Could not reconnect Google Drive. Please try again and allow the Drive permission.');
    }
  };

  const getSyncStatusIcon = () => {
    if (isSyncing) {
      return <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />;
    }
    
    if (!isOnline) {
      return <WifiOff className="w-4 h-4 text-red-500" />;
    }
    
    if (!isDriveSignedIn()) {
      return <CloudOff className="w-4 h-4 text-orange-500" />;
    }
    
    if (syncStatus?.pendingSync > 0) {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
    
    return <CheckCircle className="w-4 h-4 text-green-500" />;
  };

  const getSyncStatusText = () => {
    if (isSyncing) {
      return 'Syncing...';
    }
    
    if (!isOnline) {
      return 'Offline';
    }
    
    if (!isDriveSignedIn()) {
      return 'Drive permission needed';
    }
    
    if (syncStatus?.pendingSync > 0) {
      return `${syncStatus.pendingSync} pending`;
    }
    
    return 'Synced';
  };

  const formatLastSync = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className={`relative ${className}`}>
      <div 
        className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
        onClick={() => setShowDetails(!showDetails)}
      >
        {getSyncStatusIcon()}
        <span className="text-sm font-medium text-black">
          {getSyncStatusText()}
        </span>
        {!isOnline && (
          <WifiOff className="w-3 h-3 text-red-500" />
        )}
      </div>

      {showDetails && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 md:absolute md:inset-auto md:top-full md:right-0 md:mt-2 md:w-80 md:bg-transparent"
          onClick={() => setShowDetails(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-full max-w-sm md:max-w-none md:w-auto"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-black">Sync Status</h3>
              <button
                onClick={handleManualSync}
                disabled={!isOnline || isSyncing}
                className="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded text-sm hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>Sync Now</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  {isOnline ? (
                    <Wifi className="w-4 h-4 text-green-500" />
                  ) : (
                    <WifiOff className="w-4 h-4 text-red-500" />
                  )}
                  <span className="font-medium text-black">Connection</span>
                </div>
                <p className="text-black ml-6">
                  {isOnline ? 'Online' : 'Offline'}
                </p>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-1">
                  {isDriveSignedIn() ? (
                    <Cloud className="w-4 h-4 text-green-500" />
                  ) : (
                    <CloudOff className="w-4 h-4 text-orange-500" />
                  )}
                  <span className="font-medium text-black">Google Drive</span>
                </div>
                <p className="text-black ml-6">
                  {isDriveSignedIn() ? 'Connected' : 'Permission needed'}
                </p>
                {!isDriveSignedIn() && (
                  <button
                    onClick={handleReconnectDrive}
                    className="mt-2 text-xs text-black underline"
                  >
                    Reconnect Google Drive
                  </button>
                )}
              </div>
            </div>

            {syncStatus && (
              <div className="border-t pt-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-black">Local Records:</span>
                    <p className="text-black">{syncStatus.transactions || 0} transactions</p>
                  </div>
                  <div>
                    <span className="font-medium text-black">Pending Sync:</span>
                    <p className="text-black">{syncStatus.pendingSync || 0} items</p>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t pt-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-black">Last Sync:</span>
                <span className="text-black">{formatLastSync(lastSync)}</span>
              </div>
            </div>

            {syncStatus?.pendingSync > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-black">
                    {syncStatus.pendingSync} items waiting to sync
                  </span>
                </div>
                <p className="text-xs text-black mt-1">
                  Data will sync automatically when online and connected to Google Drive.
                </p>
              </div>
            )}

            {isOnline && !isDriveSignedIn() && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-black">
                    Google Drive permission required
                  </span>
                </div>
                <p className="text-xs text-black mt-1">
                  Grant Drive permission before adding transactions. Until this is connected, entries may only be stored on this device and can be lost if storage is cleared.
                </p>
              </div>
            )}

            {!isOnline && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <WifiOff className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-black">
                    Working Offline
                  </span>
                </div>
                <p className="text-xs text-black mt-1">
                  Your data is being saved locally and will sync when you're back online.
                </p>
              </div>
            )}
            
            {/* Mobile close button */}
            <div className="md:hidden flex justify-center pt-3 border-t">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatus;
